// Cycle-16 call-button dropdown menu. Mirrors `PingCallDropdownMenu`
// (iOS-A) / `CallDropdownMenu` (AND-A). Renders the chat header's call
// button and an absolute-positioned options menu below it; controlled
// component — host owns `isExpanded`. Mirrors the Figma `Call Dropdown`
// (`2804:27521`) and `Call status in chat` (`2444:8114`) frames.

import React from 'react';
import { Platform, Pressable, StyleSheet, Text, type TextStyle, View } from 'react-native';
import { useColorScheme } from '../foundations/colorScheme';
import { ChevronDown, Phone, Video } from 'lucide-react-native';
import { tokens } from '../design-system/tokens';
import {
  CALL_DROPDOWN_MENU_OPTIONS,
  type CallDropdownMenuOption,
} from './CallDropdownMenuOption';

export { CALL_DROPDOWN_MENU_OPTIONS, type CallDropdownMenuOption };

const OPTION_LABELS: Readonly<Record<CallDropdownMenuOption, string>> = {
  startAudioCall: 'Audio Call',
  startVideoCall: 'Video Call',
};

const OPTION_ICONS: Readonly<
  Record<CallDropdownMenuOption, typeof Phone>
> = {
  startAudioCall: Phone,
  startVideoCall: Video,
};

export interface CallDropdownMenuProps {
  isExpanded: boolean;
  onChangeExpanded: (next: boolean) => void;
  onSelect: (option: CallDropdownMenuOption) => void;
  testID?: string | undefined;
}

export const CallDropdownMenu = React.memo<CallDropdownMenuProps>(
  ({ isExpanded, onChangeExpanded, onSelect, testID }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];

    const optionTextStyle: TextStyle = { color: colors.text.primary };

    return (
      <View testID={testID}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Call options"
          accessibilityState={{ expanded: isExpanded }}
          onPress={() => onChangeExpanded(!isExpanded)}
          style={({ hovered }: { pressed: boolean; hovered?: boolean }) =>
            StyleSheet.flatten([
              styles.trigger,
              isExpanded
                ? { backgroundColor: 'rgba(255,255,255,0.10)' }
                : hovered === true
                  ? { backgroundColor: 'rgba(255,255,255,0.05)' }
                  : null,
            ])
          }
        >
          <Phone size={24} color={colors.text.primary} />
          <ChevronDown size={12} color={colors.text.primary} />
        </Pressable>
        {isExpanded ? (
          <View
            style={[
              styles.menu,
              { backgroundColor: colors.surface.tertiary },
            ]}
            accessibilityRole="menu"
          >
            {CALL_DROPDOWN_MENU_OPTIONS.map((option) => {
              const Icon = OPTION_ICONS[option];
              return (
                <Pressable
                  key={option}
                  accessibilityRole="menuitem"
                  accessibilityLabel={OPTION_LABELS[option]}
                  onPress={() => {
                    onChangeExpanded(false);
                    onSelect(option);
                  }}
                  style={({ hovered }: { pressed: boolean; hovered?: boolean }) =>
                    StyleSheet.flatten([
                      styles.menuItem,
                      hovered === true
                        ? { backgroundColor: 'rgba(255,255,255,0.05)' }
                        : null,
                    ])
                  }
                >
                  <Icon size={18} color={colors.text.primary} />
                  <Text style={[styles.menuItemText, optionTextStyle]}>
                    {OPTION_LABELS[option]}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        ) : null}
      </View>
    );
  },
);

CallDropdownMenu.displayName = 'CallDropdownMenu';

const styles = StyleSheet.create({
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    width: 42,
    height: 28,
    padding: 2,
    borderRadius: tokens.radius.sm,
  },
  menu: {
    marginTop: tokens.spacing.xs,
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
    minWidth: 149,
    // RN >=0.76 prefers `boxShadow`; on macOS the native renderer
    // falls back to elevation. We keep elevation for that path.
    ...Platform.select({
      web: {
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      } as any,
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 12,
        elevation: 4,
      },
    }),
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: 149,
    height: 30,
    padding: 4,
    borderRadius: tokens.radius.sm,
  },
  menuItemText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 16,
    fontWeight: '400',
  },
});
